import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Dropdown,
  Flex,
  Grid,
  Input,
  Layout,
  Select,
  Space,
  Table,
} from "antd";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { getRouteByName, palette } from "libs";
import { DeleteModal, Put } from "components";
import {
  CategoryInterface,
  useDeleteCategoryMutation,
  useGetCategoriesQuery,
  useGetServicesQuery,
} from "api";
import { useFilters } from "hooks";
import { CategoryForm } from "./CategoryForm";
import { CONTENT_TOPIC_LABELS } from "./contentTopics";
import { useAppSelector } from "store/hooks";

interface RolesFiltersInterface {
  "filters[name][$contains]"?: string;
  "filters[services][id][$eq]"?: number[];
}

export const CategoriesPage = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();

  // Undefined = form closed. An empty object opens it in create mode; a category
  // opens it in edit mode.
  const [categoryForm, setCategoryForm] = useState<{
    category?: CategoryInterface;
  }>();
  const [deleteCategory, setDeleteCategory] = useState<CategoryInterface>();

  const { filters, onFiltersChange } = useFilters<RolesFiltersInterface>(
    {
      "filters[name][$contains]": undefined,
      "filters[services][id][$eq]": undefined,
    },
    { keyword: "categories" }
  );

  const {
    isCategoriesRead,
    isCategoriesCreate,
    isAll,
    isCategoriesEdit,
    isCategoriesDelete,
  } = useAppSelector((state) => state.loggedAdmin.parsedPermissions);

  const { data, isLoading } = useGetCategoriesQuery(filters);

  const { data: dataServices, isLoading: isServicesLoading } =
    useGetServicesQuery({});

  const [deleteRoleMutation, { isLoading: isDeleteLoading }] =
    useDeleteCategoryMutation();

  const breakpoints = Grid.useBreakpoint();

  useEffect(() => {
    if (!isCategoriesRead && !isAll) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [isCategoriesRead, isAll, navigate]);

  const usersColumns = useMemo<ColumnsType<CategoryInterface>>(
    () => [
      {
        title: "Name",
        dataIndex: "name",
      },
      {
        title: "Topic stiri",
        dataIndex: "topic",
        render: (topic: CategoryInterface["topic"]) => (
          <Put condition={!!topic}>{topic && CONTENT_TOPIC_LABELS[topic]}</Put>
        ),
      },
      {
        title: "Servicii",
        align: "right",
        render: (_, category) => (
          <Space>{category.services.map((i) => i.name).join(", ")}</Space>
        ),
      },
      {
        title: "Actions",
        align: "end",
        width: 100,
        render: (_, category) => {
          const items = [
            ...(isAll || isCategoriesEdit
              ? [
                  {
                    label: "Modifica",
                    key: "edit",
                    onClick: () => setCategoryForm({ category }),
                  },
                ]
              : []),
            ...(isAll || isCategoriesDelete
              ? [
                  {
                    label: "Delete",
                    key: "delete",
                    onClick: () => setDeleteCategory(category),
                  },
                ]
              : []),
          ];

          return items.length ? (
            <Dropdown menu={{ items }} trigger={["click"]} arrow>
              <MoreOutlined style={{ padding: 3 }} />
            </Dropdown>
          ) : null;
        },
      },
    ],
    [isCategoriesEdit, isCategoriesDelete, isAll]
  );

  const onDeleteOkClick = () =>
    deleteRoleMutation(deleteCategory?.id)
      .then(() =>
        notification.success({
          message: "Categoria a fost sters cu success",
        })
      )
      .catch(() =>
        notification.error({ message: "Categoria nu poate fi sters!" })
      )
      .finally(() => setDeleteCategory(undefined));

  return (
    <>
      <Flex
        justify="space-between"
        style={{
          marginBottom: "1rem",
          flexDirection: breakpoints.xs ? "column-reverse" : undefined,
        }}
        gap={8}
      >
        <Space wrap>
          <Input.Search
            placeholder="Cauta denumire"
            value={filters["filters[name][$contains]"]}
            onChange={(e) =>
              onFiltersChange({ "filters[name][$contains]": e.target.value })
            }
            allowClear
          />

          <Select
            loading={isServicesLoading}
            className="capitalize"
            placeholder="Servicii"
            value={filters["filters[services][id][$eq]"]}
            onChange={(services) =>
              onFiltersChange({ "filters[services][id][$eq]": services })
            }
            style={{ minWidth: 150 }}
            allowClear
          >
            {dataServices?.map((i) => (
              <Select.Option key={i.id} value={i.id} className="capitalize">
                {i.name}
              </Select.Option>
            ))}
          </Select>
        </Space>
        {(isAll || isCategoriesCreate) && (
          <Button
            type="primary"
            onClick={() => setCategoryForm({})}
            style={{ color: "white" }}
            icon={<PlusOutlined />}
          >
            Adauga categorie
          </Button>
        )}
      </Flex>
      <Layout style={{ backgroundColor: palette.white }}>
        <Table
          columns={usersColumns}
          loading={isLoading}
          dataSource={data}
          rowKey="id"
          scroll={{ x: 500 }}
        />
      </Layout>

      {categoryForm && (
        <CategoryForm
          category={categoryForm.category}
          onCancel={() => setCategoryForm(undefined)}
        />
      )}

      {deleteCategory?.id && (
        <DeleteModal
          title="Sterge rol"
          onCancel={() => setDeleteCategory(undefined)}
          onOk={onDeleteOkClick}
          okButtonProps={{ loading: isDeleteLoading }}
        >
          Esti sigur ca vrei sa stergi categoria {deleteCategory?.name}
        </DeleteModal>
      )}
    </>
  );
};
